import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Wrench, Github, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MaintainProject = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    repoUrl: '',
    taskDescription: '',
    taskType: 'bug_fix'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const taskTypes = [
    { value: 'bug_fix', label: '🐛 Bug Fix', desc: 'Fix a specific bug or issue' },
    { value: 'add_feature', label: '✨ Add Feature', desc: 'Add new functionality' },
    { value: 'refactor', label: '🔨 Refactor', desc: 'Improve code quality' },
    { value: 'update_deps', label: '📦 Update Dependencies', desc: 'Update packages and libraries' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/maintenance/tasks`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          repo_url: formData.repoUrl,
          task_description: formData.taskDescription,
          task_type: formData.taskType
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create maintenance task');
      }

      const task = await response.json();
      navigate(`/maintenance/${task.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container" data-testid="maintain-project-page">
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '3rem' }}>
          <Link to="/" className="btn btn-secondary" style={{ marginBottom: '1.5rem' }}>
            <ArrowLeft size={18} />
            Back to Dashboard
          </Link>
          <h1 style={{ fontSize: '2.5rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Wrench size={40} style={{ color: 'hsl(var(--accent))' }} />
            Maintain Existing App
          </h1>
          <p style={{ color: 'hsl(var(--muted-foreground))' }}>
            Import a repository and let our agents analyze and improve it
          </p>
        </div>

        {/* Info Banner */}
        <div style={{ 
          background: 'hsl(var(--accent) / 0.1)', 
          border: '1px solid hsl(var(--accent))',
          borderRadius: 'var(--radius)',
          padding: '1rem',
          marginBottom: '2rem',
          display: 'flex',
          gap: '1rem',
          alignItems: 'start'
        }}>
          <AlertCircle size={20} style={{ color: 'hsl(var(--accent))', flexShrink: 0, marginTop: '2px' }} />
          <div style={{ fontSize: '0.875rem' }}>
            <strong style={{ color: 'hsl(var(--accent))' }}>AI-Powered Codebase Assistant</strong>
            <p style={{ color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem', marginBottom: 0 }}>
              Connect your repo and chat with AI to analyze, fix bugs, and generate code changes. Creates pull requests automatically.
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="card">
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label className="label" htmlFor="repoUrl">
                <Github size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                Repository URL
              </label>
              <input
                id="repoUrl"
                type="url"
                className="input"
                placeholder="https://github.com/username/repository"
                value={formData.repoUrl}
                onChange={(e) => setFormData({ ...formData, repoUrl: e.target.value })}
                required
                data-testid="repo-url-input"
              />
              <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>
                Enter the GitHub repository URL you want to work on
              </p>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label className="label">Task Type</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
                {taskTypes.map(type => (
                  <div
                    key={type.value}
                    onClick={() => setFormData({ ...formData, taskType: type.value })}
                    style={{
                      padding: '1rem',
                      border: `2px solid ${formData.taskType === type.value ? 'hsl(var(--accent))' : 'hsl(var(--border))'}`,
                      borderRadius: 'var(--radius)',
                      cursor: 'pointer',
                      background: formData.taskType === type.value ? 'hsl(var(--accent) / 0.05)' : 'transparent',
                      transition: 'all 0.2s'
                    }}
                    data-testid={`task-type-${type.value}`}
                  >
                    <div style={{ fontWeight: '600', marginBottom: '0.25rem', color: formData.taskType === type.value ? 'hsl(var(--accent))' : 'inherit' }}>
                      {type.label}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                      {type.desc}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <label className="label" htmlFor="taskDescription">
                Task Description
              </label>
              <textarea
                id="taskDescription"
                className="input"
                placeholder="Describe what needs to be fixed or improved. Be specific!

Examples:
- Fix the login timeout issue - users getting logged out after 5 minutes
- Add dark mode toggle to the settings page
- Refactor the authentication module to use JWT instead of sessions
- Update React to version 18 and fix compatibility issues"
                value={formData.taskDescription}
                onChange={(e) => setFormData({ ...formData, taskDescription: e.target.value })}
                required
                rows={8}
                data-testid="task-description-input"
              />
              <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>
                Provide detailed context about what needs to be done and why
              </p>
            </div>

            {error && (
              <div style={{ 
                padding: '1rem', 
                background: 'hsl(var(--destructive) / 0.1)', 
                border: '1px solid hsl(var(--destructive))',
                borderRadius: 'var(--radius)',
                marginBottom: '1.5rem',
                color: 'hsl(var(--destructive))'
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
              style={{ width: '100%', justifyContent: 'center' }}
              data-testid="submit-btn"
            >
              {loading ? (
                <>
                  <div className="spinner" />
                  Analyzing Repository...
                </>
              ) : (
                <>
                  <Wrench size={18} />
                  Start Maintenance
                </>
              )}
            </button>
          </form>
        </div>

        {/* What Happens Next */}
        <div style={{ 
          marginTop: '2rem', 
          padding: '1.5rem', 
          background: 'hsl(var(--muted))', 
          borderRadius: 'var(--radius)',
          border: '1px solid hsl(var(--border))'
        }}>
          <h3 style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>What happens next?</h3>
          <ol style={{ paddingLeft: '1.5rem', fontSize: '0.875rem', lineHeight: '1.8', color: 'hsl(var(--muted-foreground))' }}>
            <li><strong>Repository Import Agent</strong> clones and analyzes your repository</li>
            <li><strong>Code Analysis Agent</strong> understands the codebase structure and identifies relevant files</li>
            <li><strong>Chat with AI</strong> to discuss, plan, and request code changes using RAG-powered context</li>
            <li><strong>Review Changes</strong> in the Changes tab before committing</li>
            <li><strong>Create Pull Request</strong> to push AI-generated changes to your repo</li>
          </ol>
        </div>

        {/* Feature Comparison */}
        <div style={{ marginTop: '2rem', padding: '1.5rem', background: 'hsl(var(--card))', borderRadius: 'var(--radius)', border: '1px solid hsl(var(--border))' }}>
          <h3 style={{ fontSize: '1rem', marginBottom: '1rem' }}>Maintenance vs. New Build</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.875rem' }}>
            <div>
              <div style={{ fontWeight: '600', marginBottom: '0.5rem', color: 'hsl(var(--accent))' }}>
                🆕 New Build
              </div>
              <ul style={{ paddingLeft: '1.25rem', color: 'hsl(var(--muted-foreground))', lineHeight: '1.6' }}>
                <li>Starts from scratch</li>
                <li>Generates full codebase</li>
                <li>Creates new repository</li>
                <li>Deploys to new URL</li>
              </ul>
            </div>
            <div>
              <div style={{ fontWeight: '600', marginBottom: '0.5rem', color: 'hsl(var(--accent))' }}>
                🔧 Maintenance
              </div>
              <ul style={{ paddingLeft: '1.25rem', color: 'hsl(var(--muted-foreground))', lineHeight: '1.6' }}>
                <li>Works on existing code</li>
                <li>Analyzes and modifies</li>
                <li>Creates pull requests</li>
                <li>Preserves existing work</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintainProject;
